/**
 * ═══════════════════════════════════════════════════════════════════════════
 * RiskFactorCard - Card Individual de Factor de Riesgo
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * @file RiskFactorCard.tsx
 * @version 1.0.0
 * @description Card que muestra un factor individual del Risk Meter con
 *              nombre, descripción, score, status, progress bar y recomendación
 * 
 * @component RiskFactorCard
 * @architecture Fase 2 - Componentes Visuales del Risk Meter
 * 
 * FEATURES:
 * - Card responsive con React Bootstrap
 * - Badge de status con colores (excellent/good/warning/danger)
 * - Progress bar visual del score
 * - Expandible para mostrar recomendaciones
 * - Iconos según status
 * - Animaciones smooth
 * 
 * STATUS COLORS:
 * - excellent: #22c55e (verde brillante)
 * - good:      #10b981 (verde)
 * - warning:   #f59e0b (amarillo/naranja)
 * - danger:    #ef4444 (rojo)
 */

import React, { useState } from 'react';
import { Card, Badge, ProgressBar, Button, Collapse } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCheckCircle, 
  faExclamationTriangle, 
  faTimesCircle,
  faInfoCircle,
  faChevronDown,
  faChevronUp
} from '@fortawesome/free-solid-svg-icons';

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

type FactorStatus = 'excellent' | 'good' | 'warning' | 'danger';

interface WhatsAppRiskFactor {
  id: string;
  name: string;
  description: string;
  weight: number;
  currentValue: any;
  optimalRange: any;
  score: number;
  status: FactorStatus;
  recommendation?: string;
}

interface RiskFactorCardProps {
  /** Factor de riesgo a mostrar */
  factor: WhatsAppRiskFactor;
  
  /** Estado inicial de expansión */
  expanded?: boolean;
  
  /** Callback cuando se expande/colapsa */
  onToggle?: () => void;
  
  /** Clase CSS adicional */
  className?: string;
  
  /** Mostrar peso del factor */
  showWeight?: boolean;
}

// ═══════════════════════════════════════════════════════════════════════════
// CONSTANTES
// ═══════════════════════════════════════════════════════════════════════════

const STATUS_COLORS: Record<FactorStatus, string> = {
  excellent: '#00BDA5', // Aqua turquesa profesional (HubSpot/Mac)
  good: '#0091AE',      // Azul aqua profundo
  warning: '#FF7A59',   // Coral warning (HubSpot)
  danger: '#F2545B'     // Rojo suave profesional
};

const STATUS_BG_COLORS: Record<FactorStatus, string> = {
  excellent: 'success',
  good: 'info',
  warning: 'warning',
  danger: 'danger'
};

const STATUS_LABELS: Record<FactorStatus, string> = {
  excellent: 'Excelente',
  good: 'Bueno',
  warning: 'Advertencia',
  danger: 'Peligro'
};

const STATUS_ICONS: Record<FactorStatus, any> = {
  excellent: faCheckCircle,
  good: faCheckCircle,
  warning: faExclamationTriangle,
  danger: faTimesCircle
};

// ═══════════════════════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Obtiene el variant de Bootstrap para el ProgressBar según el status
 */
const getProgressVariant = (status: FactorStatus): string => {
  const variants: Record<FactorStatus, string> = {
    excellent: 'success',
    good: 'info',
    warning: 'warning',
    danger: 'danger'
  };
  return variants[status];
};

/**
 * Formatea el valor actual para display
 */
const formatCurrentValue = (value: any, id: string): string => {
  if (value === null || value === undefined) return 'N/A';
  
  // Casos especiales según el ID del factor
  if (id === 'messages_per_hour') return `${value} msg/hora`;
  if (id === 'list_size') return `${value} contactos`;
  if (id === 'message_length') return `${value} caracteres`;
  if (id === 'emojis') return `${value} emojis`;
  if (id === 'links') return `${value} enlaces`;
  if (id === 'variables') return `${value} variables`;
  if (id === 'first_response_ai') return value ? 'Habilitada' : 'Deshabilitada';
  if (id === 'whatsapp_status') return value ? 'Configurado' : 'Sin configurar';
  
  return String(value);
};

/**
 * Formatea el rango óptimo para display según su estructura
 */
const formatOptimalRange = (range: any, factorId: string): string => {
  if (!range) return 'N/A';
  
  // Tipo { min, max, ideal } - Factores numéricos
  if ('min' in range && 'max' in range && 'ideal' in range) {
    if (factorId === 'links') {
      return '0 enlaces (sin enlaces)';
    }
    if (factorId === 'emojis') {
      return `0-${range.max} emojis (ideal: ${range.ideal})`;
    }
    if (factorId === 'messages_per_hour') {
      return `${range.min}-${range.max} msg/hora (ideal: ${range.ideal})`;
    }
    if (factorId === 'list_size') {
      return `${range.min}-${range.max} contactos (ideal: ${range.ideal})`;
    }
    if (factorId === 'message_length') {
      return `${range.min}-${range.max} caracteres (ideal: ${range.ideal})`;
    }
    if (factorId === 'variables') {
      return `${range.min}-${range.max} variables (ideal: ${range.ideal})`;
    }
    return `${range.min}-${range.max} (ideal: ${range.ideal})`;
  }
  
  // Tipo { start, end } - Horarios
  if ('start' in range && 'end' in range) {
    const startStr = range.start.toString().padStart(2, '0');
    const endStr = range.end.toString().padStart(2, '0');
    return `${startStr}:00 - ${endStr}:00`;
  }
  
  // Tipo { optimal, avoid } - Días
  if ('optimal' in range && 'avoid' in range) {
    return `${range.optimal} (evitar: ${range.avoid})`;
  }
  
  // Tipo { optimal } - Simple
  if ('optimal' in range) {
    return range.optimal;
  }
  
  return String(range);
};

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

export const RiskFactorCard: React.FC<RiskFactorCardProps> = ({
  factor,
  expanded: initialExpanded = false,
  onToggle,
  className = '',
  showWeight = true
}) => {
  // ───────────────────────────────────────────────────────────────────────
  // STATE
  // ───────────────────────────────────────────────────────────────────────
  
  const [isExpanded, setIsExpanded] = useState(initialExpanded);
  
  // ───────────────────────────────────────────────────────────────────────
  // HANDLERS
  // ───────────────────────────────────────────────────────────────────────
  
  const handleToggle = () => {
    setIsExpanded(!isExpanded);
    if (onToggle) onToggle();
  };
  
  // ───────────────────────────────────────────────────────────────────────
  // RENDER HELPERS
  // ───────────────────────────────────────────────────────────────────────
  
  const statusColor = STATUS_COLORS[factor.status];
  const statusBgVariant = STATUS_BG_COLORS[factor.status];
  const statusLabel = STATUS_LABELS[factor.status];
  const statusIcon = STATUS_ICONS[factor.status];
  const progressVariant = getProgressVariant(factor.status);
  
  const hasRecommendation = Boolean(factor.recommendation);
  
  // ───────────────────────────────────────────────────────────────────────
  // RENDER
  // ───────────────────────────────────────────────────────────────────────
  
  return (
  <Card 
    className={`risk-factor-card ${className}`}
    style={{
      border: '1px solid rgba(0,0,0,0.06)',
      borderRadius: '12px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
      backgroundColor: 'var(--bs-body-bg)',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      overflow: 'hidden',
      height: '100%'
    }}
  >
    {/* Header */}
    <Card.Header
      style={{
        backgroundColor: 'var(--bs-body-bg)',
        borderBottom: `2px solid ${statusColor}`,
        padding: '14px 18px',
        cursor: hasRecommendation ? 'pointer' : 'default'
      }}
      onClick={hasRecommendation ? handleToggle : undefined}
    >
      <div className="d-flex align-items-start justify-content-between gap-2">
        <div style={{ flex: 1 }}>
          {/* Título con icono */}
          <div className="d-flex align-items-center gap-2 mb-1">
            <div 
              style={{
                width: '28px',
                height: '28px',
                borderRadius: '6px',
                backgroundColor: `${statusColor}15`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}
            >
              <FontAwesomeIcon 
                icon={statusIcon} 
                style={{ color: statusColor, fontSize: '0.875rem' }}
              />
            </div>
            <h6 
              className="mb-0" 
              style={{ 
                fontWeight: 600,
                fontSize: '0.9rem',
                color: 'var(--bs-body-color)',
                letterSpacing: '-0.01em'
              }}
            >
              {factor.name}
            </h6>
            {showWeight && (
              <span 
                style={{ 
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  color: 'var(--bs-secondary)',
                  marginLeft: 'auto'
                }}
              >
                {Math.round(factor.weight * 100)}%
              </span>
            )}
          </div>
          
          {/* Descripción */}
          <p 
            style={{ 
              fontSize: '0.8rem',
              color: 'var(--bs-secondary)',
              marginBottom: 0,
              marginLeft: '36px',
              lineHeight: '1.4'
            }}
          >
            {factor.description}
          </p>
        </div>
        
        {/* Status Badge */}
        <Badge 
          bg=""
          style={{
            fontSize: '0.7rem',
            padding: '4px 10px',
            fontWeight: 600,
            borderRadius: '12px',
            backgroundColor: `${statusColor}`,
            color: '#fff',
            boxShadow: `0 2px 6px ${statusColor}35`,
            flexShrink: 0
          }}
        >
          {statusLabel}
        </Badge>
      </div>
    </Card.Header>
    
    {/* Body */}
    <Card.Body style={{ padding: '18px' }}>
      {/* Score section */}
      <div className="mb-3">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div>
            <span style={{ 
              fontSize: '0.7rem', 
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              color: 'var(--bs-secondary)'
            }}>
              Valor Actual
            </span>
            <div style={{ 
              fontSize: '1.1rem', 
              fontWeight: 700,
              color: 'var(--bs-body-color)',
              marginTop: '2px'
            }}>
              {formatCurrentValue(factor.currentValue, factor.id)}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <span style={{ 
              fontSize: '0.7rem', 
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              color: 'var(--bs-secondary)',
              display: 'block',
              marginBottom: '2px'
            }}>
              Score
            </span>
            <span 
              style={{ 
                fontSize: '1.75rem', 
                fontWeight: 800,
                color: statusColor,
                lineHeight: 1,
                letterSpacing: '-0.02em'
              }}
            >
              {factor.score}
            </span>
            <span style={{ 
              fontSize: '0.8rem',
              fontWeight: 600,
              color: 'var(--bs-secondary)',
              marginLeft: '2px'
            }}>
              /100
            </span>
          </div>
        </div>
        
        {/* Progress Bar */}
        <div style={{ 
          backgroundColor: '#f1f3f5',
          borderRadius: '6px',
          height: '8px',
          overflow: 'hidden',
          position: 'relative'
        }}>
          <div
            style={{
              width: `${factor.score}%`,
              height: '100%',
              background: `linear-gradient(90deg, ${statusColor} 0%, ${statusColor}dd 100%)`,
              borderRadius: '6px',
              transition: 'width 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
              boxShadow: `0 0 8px ${statusColor}35`
            }}
          />
        </div>
      </div>
      
      {/* Rango óptimo */}
      {factor.optimalRange && (
        <div 
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            padding: '8px 12px',
            backgroundColor: `${statusColor}08`,
            border: `1px solid ${statusColor}20`,
            borderRadius: '8px',
            fontSize: '0.8rem',
            fontWeight: 500,
            color: 'var(--bs-body-color)'
          }}
        >
          <FontAwesomeIcon 
            icon={faInfoCircle} 
            style={{ color: statusColor, fontSize: '0.85rem' }}
          />
          <span>
            <strong style={{ fontWeight: 600 }}>Óptimo:</strong>{' '}
            {formatOptimalRange(factor.optimalRange, factor.id)}
          </span>
        </div>
      )}
      
      {/* Botón de recomendación */}
      {hasRecommendation && (
        <Button
          variant="link"
          size="sm"
          onClick={handleToggle}
          style={{
            marginTop: '12px',
            padding: '6px 0',
            fontSize: '0.8rem',
            fontWeight: 600,
            textDecoration: 'none',
            color: statusColor,
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}
        >
          <FontAwesomeIcon 
            icon={isExpanded ? faChevronUp : faChevronDown}
            style={{ fontSize: '0.75rem' }}
          />
          {isExpanded ? 'Ocultar' : 'Ver'} recomendación
        </Button>
      )}
      
      {/* Recomendación colapsable */}
      {hasRecommendation && (
        <Collapse in={isExpanded}>
          <div>
            <div 
              style={{
                marginTop: '12px',
                padding: '12px',
                backgroundColor: `${statusColor}08`,
                border: `1px solid ${statusColor}20`,
                borderRadius: '8px',
                fontSize: '0.825rem',
                lineHeight: '1.5'
              }}
            >
              <div style={{ 
                display: 'flex',
                alignItems: 'flex-start',
                gap: '8px'
              }}>
                <span style={{ fontSize: '1.1rem' }}>💡</span>
                <div>
                  <strong style={{ 
                    color: statusColor,
                    fontWeight: 600,
                    display: 'block',
                    marginBottom: '4px'
                  }}>
                    Recomendación
                  </strong>
                  <p style={{ marginBottom: 0, color: 'var(--bs-body-color)' }}>
                    {factor.recommendation}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </Collapse>
      )}
    </Card.Body>
    
    <style>{`
      .risk-factor-card:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0,0,0,0.12) !important;
        border-color: rgba(0,0,0,0.1) !important;
      }
    `}</style>
  </Card>
);
};

// ═══════════════════════════════════════════════════════════════════════════
// EXPORT
// ═══════════════════════════════════════════════════════════════════════════

export default RiskFactorCard;
